import type { Localized } from '../types'

export type CuratedFeedback = {
  id: string
  author: string
  avatar: string
  gameId: string
  created: string
  topic: Localized
  text: Localized
}

// Editorial playtest examples, not user testimonials. Keeping these in source
// makes their origin explicit and lets the page stay useful before local notes
// are added in a visitor's browser.
export const curatedFeedback: CuratedFeedback[] = [
  {
    id: 'tideglass-decision-trail', author: 'HarborFox', avatar: '🦊', gameId: 'tideglass-dispatch', created: '2026-08-28T08:43:00+08:00',
    topic: { en: 'Strategy idea', zh: '策略建议' },
    text: {
      en: 'I intentionally sent a barge that fit the current tide and finally understood the trap. A one-move decision trail could name the deadline I would strand, then fade after the next correct dispatch.',
      zh: '我故意派出了一艘“当前能走”的驳船，终于看懂了这个陷阱。建议增加一条只保留一步的决策记录，点明会被卡住的是哪个截止窗口，下一次正确调度后再淡出。',
    },
  },
  {
    id: 'compass-preview-tempo', author: 'NorthStar', avatar: '🧭', gameId: 'compass-chorus', created: '2026-08-28T00:17:00+08:00',
    topic: { en: 'First-run pacing', zh: '首次节奏' },
    text: {
      en: 'The rotation rule is satisfying once it clicks. Could round one preview each arrow a little more slowly, then return to the current tempo from round two so new players learn without lowering the later challenge?',
      zh: '理解旋转规则之后很上头。第一轮能否把每个箭头的展示稍微放慢一点，从第二轮恢复当前速度？这样新人能学会规则，又不会降低后面的挑战。',
    },
  },
  {
    id: 'windline-training-ghost', author: 'KiteMoth', avatar: '🪁', gameId: 'windline-courier', created: '2026-08-27T21:26:00+08:00',
    topic: { en: 'Training mode', zh: '练习模式' },
    text: {
      en: 'The signed gust math is clever, but mobile taps can feel like guessing at first. A practice-only landing ghost after changing tether length would teach the relationship without solving the scored route.',
      zh: '带正负号的阵风计算很巧妙，但手机上刚开始容易像在猜。建议只在练习模式里，调整线长后显示一次落点残影，帮助理解关系，同时不替正式计分路线解题。',
    },
  },
  {
    id: 'harbor-reduced-motion', author: 'CalmCrane', avatar: '🏗️', gameId: 'harbor-handoff', created: '2026-08-27T13:08:00+08:00',
    topic: { en: 'Accessibility', zh: '无障碍建议' },
    text: {
      en: 'The narrow perfect zone gives me a reason to retry. Please add a reduced-motion option that keeps the timing marker but softens the crane swing and success flash for longer sessions.',
      zh: '完美判定区很窄，让人很想再来一局。建议加入“减少动态效果”选项：保留计时标记，但减弱吊机摆动和成功闪光，长时间玩会更舒服。',
    },
  },
  {
    id: 'signal-lens-reminder', author: 'LeafByte', avatar: '🌿', gameId: 'signal-orchard', created: '2026-08-26T22:41:00+08:00',
    topic: { en: 'Readable rules', zh: '规则可读性' },
    text: {
      en: 'Switching between color and shape lenses is the fun surprise. A tiny COLOR or SHAPE label beside each receiver would stop my eyes jumping back to the header on a small screen.',
      zh: '颜色与形状透镜来回切换是最好玩的变化。小屏幕上如果每个接收器旁边也显示一个小小的“颜色”或“形状”，视线就不用总跳回顶部。',
    },
  },
  {
    id: 'prism-last-rotation', author: 'GlassFinch', avatar: '🔆', gameId: 'prism-beacon', created: '2026-08-26T09:53:00+08:00',
    topic: { en: 'Puzzle feedback', zh: '解谜反馈' },
    text: {
      en: 'I like that the beam updates immediately. After a rotation, briefly outline the mirror number I just changed; it would make multi-step experiments easier to review without adding a full undo button.',
      zh: '我喜欢光束会立刻更新。每次旋转后，可以短暂描边刚刚操作的镜片编号；这样多步试验更容易复盘，又不需要直接加入完整撤销。',
    },
  },
  {
    id: 'cartographer-patterns', author: 'MapOtter', avatar: '🦦', gameId: 'pocket-cartographer', created: '2026-08-25T19:34:00+08:00',
    topic: { en: 'Visual clarity', zh: '视觉辨识' },
    text: {
      en: 'The tiny map feels surprisingly adventurous. Terrain hatching—dots for meadow, waves for water, short lines for hills—would help color-blind players and make remembered routes easier to describe.',
      zh: '小地图居然很有探险感。建议给地形增加纹理：草地用点、水面用波纹、山地用短线，既方便色觉差异玩家，也更容易描述记住的路线。',
    },
  },
  {
    id: 'pattern-vault-history', author: 'QuietComet', avatar: '☄️', gameId: 'pattern-vault', created: '2026-08-24T11:16:00+08:00',
    topic: { en: 'History scan', zh: '历史记录' },
    text: {
      en: 'The scan token makes the final guess tense. It would be useful if the guess history kept the exact-match and near-match symbols aligned in columns, especially once several attempts are visible.',
      zh: '扫描令牌让最后一猜很紧张。建议历史记录中的“完全匹配”和“接近匹配”图标始终按列对齐，尝试次数多以后会更容易比较。',
    },
  },
  {
    id: 'breakout-touch-offset', author: 'BrickRaccoon', avatar: '🦝', gameId: 'breakout', created: '2026-08-23T20:07:00+08:00',
    topic: { en: 'Touch control', zh: '触控建议' },
    text: {
      en: 'Dragging the paddle works well, but my finger covers it near the bottom edge. An optional touch offset that places the paddle slightly above the finger would make the last-ball moments much fairer.',
      zh: '拖动挡板很顺手，但手指靠近底边时会挡住挡板。建议提供可选的触控偏移，让挡板保持在手指上方一点，最后一球会公平很多。',
    },
  },
]
